import { css, cx } from "@emotion/css";
import type { PageAction } from "@octopusdeploy/design-system-components";
import { ActionButtonType, Button } from "@octopusdeploy/design-system-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionTemplateParameterResource, PlatformHubAccountResource, ProcessTemplateParameterResource, ProcessTemplateParameterValueResource, ScopeValues } from "@octopusdeploy/octopus-server-client";
import { AccountType, ControlType, Permission } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import * as React from "react";
import { useMemo, useState } from "react";
import ProcessTemplateParameterEditorDialog from "~/areas/blueprints/ProcessTemplateParameterEditorDialog";
import { useScopeWidth } from "~/areas/blueprints/useScopeWidth";
import ReadonlyInlineVariableValue from "~/areas/projects/components/Variables/TenantVariables/ReadonlyInlineVariableValue";
import { TemplateHelpText } from "~/areas/projects/components/Variables/TenantVariables/TemplateHelpText";
import { TemplateName } from "~/areas/projects/components/Variables/TenantVariables/TemplateName";
import { getVariableTypeFromDisplaySettings } from "~/areas/projects/components/Variables/TenantVariables/getVariableTypeFromDisplaySettings";
import type { ScopeSpecification } from "~/areas/variables/ReadonlyVariableResource/index";
import VariableScopeCell from "~/areas/variables/VariableScopeCell/index";
import { SortActionTemplateParametersButton } from "~/components/ActionTemplateParametersList/index";
import CheckboxInput from "~/components/ControlInputs/CheckboxInput";
import MultiLineTextInput from "~/components/ControlInputs/MultiLineTextInput";
import SelectInput from "~/components/ControlInputs/SelectInput";
import SingleLineTextInput from "~/components/ControlInputs/SingleLineTextInput";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import DialogOpener from "~/components/Dialog/DialogOpener";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import OnboardingPage from "~/components/GettingStarted/OnboardingPage";
import { GroupedDataTable } from "~/components/GroupedDataTable/GroupedDataTable";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import PermissionCheck, { hasPermission } from "~/components/PermissionCheck/PermissionCheck";
import PopoverWhenFocused from "~/components/PopoverWhenFocused/index";
import { Select, Text } from "~/components/form/index";
import selectOptionsToItems from "~/primitiveComponents/form/Select/Options";
import { DebounceText } from "~/primitiveComponents/form/Text/Text";
interface ProcessTemplateParametersTabProps {
    parameters: ProcessTemplateParameterResource[];
    onChange: (parameters: ActionTemplateParameterResource[]) => void;
}
export function ProcessTemplateParametersTab({ parameters, onChange }: ProcessTemplateParametersTabProps) {
    const { result: environments } = useQuery((repository) => repository.PlatformHub.getEnvironmentSummaries(), [], "Get environment summaries");
    const { result: platformHubAccounts } = useQuery((repository) => repository.PlatformHub.getAllAccounts(), [], "Get all platform hub accounts");
    const availableScopeValues = useMemo(() => ({ ...defaultScopeValues, Environments: environments?.map((e) => ({ Id: `${e.SpaceId}/${e.Id}`, Name: `${e.Name} (${e.SpaceName})` })) ?? [] }), [environments]);
    const onAdd = (parameter: ActionTemplateParameterResource) => onChange([...parameters, parameter]);
    if (parameters.length === 0) {
        return <ParametersOnboarding onAdd={onAdd} parameters={parameters}/>;
    }
    if (!platformHubAccounts || !environments) {
        return null;
    }
    return <ProcessTemplateParametersTable parameters={parameters} onChange={onChange} availableScopeValues={availableScopeValues} platformHubAccounts={platformHubAccounts}/>;
}
export type ParameterValueRow = {
    Parameter: ProcessTemplateParameterResource;
    Value: ProcessTemplateParameterValueResource | null;
    ParameterIndex: number;
    ValueIndex: number;
};
interface ProcessTemplateParametersTableProps extends ProcessTemplateParametersTabProps {
    availableScopeValues: ScopeValues;
    platformHubAccounts: PlatformHubAccountResource[];
}
function ProcessTemplateParametersTable({ parameters, onChange, availableScopeValues, platformHubAccounts }: ProcessTemplateParametersTableProps) {
    const [parameterToEdit, setParameterToEdit] = React.useState<ProcessTemplateParameterResource | null>(null);
    const [parameterToEditIndex, setParameterToEditIndex] = React.useState<number | null>(null);
    const [focusedValueKey, setFocusedValueKey] = useState<ProcessTemplateValueKey | null>(null);
    const [isEditingScope, setIsEditingScope] = useState(false);
    const { scopeHeaderRef, scopeWidth } = useScopeWidth();
    const setEditingParameter = (parameter: ProcessTemplateParameterResource | null) => {
        setParameterToEdit(parameter);
        setParameterToEditIndex(parameter ? parameters.indexOf(parameter) : null);
    };
    const onParameterChange = (newParameter: ProcessTemplateParameterResource, parameterIndex: number) => {
        onChange(parameters.map((p, i) => (i === parameterIndex ? newParameter : p)));
    };
    const deleteParameter = (parameterIndex: number) => {
        onChange(parameters.filter((_, i) => i !== parameterIndex));
    };
    const deleteValue = (parameterIndex: number, valueIndex: number) => {
        onChange(parameters.map((p, i) => {
            if (i === parameterIndex) {
                return {
                    ...p,
                    Values: p.Values.filter((_, j) => j !== valueIndex),
                };
            }
            return p;
        }));
    };
    const parameterValues: ParameterValueRow[] = parameters.flatMap((p, parameterIndex): ParameterValueRow[] => {
        return p.Values.map<ParameterValueRow>((v, valueIndex) => ({ Parameter: p, Value: v, ParameterIndex: parameterIndex, ValueIndex: valueIndex })).concat([
            { Parameter: p, Value: null, ParameterIndex: parameterIndex, ValueIndex: p.Values.length },
        ]);
    });
    return (<div>
            <DialogOpener open={!!parameterToEdit} onClose={() => setEditingParameter(null)}>
                {parameterToEdit ? <ProcessTemplateParameterEditorDialog allParameters={parameters} parameter={parameterToEdit} onOk={(parameter) => onChange(parameters.map((p, i) => (i === parameterToEditIndex ? parameter : p)))}/> : null}
            </DialogOpener>
            <GroupedDataTable data={parameterValues} columns={[
            {
                title: "Name",
                render: (data) => (data.ValueIndex === 0 ? <ProcessTemplateParameterNameCell data={data} onOpenEditParameterDialog={setEditingParameter} onDeleteParameter={deleteParameter}/> : null),
                width: "25%",
                isChild: (data) => data.ValueIndex !== 0,
                padding: space[12],
            },
            {
                title: "Value",
                render: (data) => (<ProcessTemplateParameterValueCell parameter={data.Parameter} value={data.Value} onChange={(parameter) => onParameterChange(parameter, data.ParameterIndex)} valueIndex={data.ValueIndex} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} platformHubAccounts={platformHubAccounts}/>),
                width: "35%",
                padding: "0",
            },
            {
                title: "Scope",
                render: (data) => data.Value ? (<div className={styles.scopeAndOverflowMenuContainer}>
                                    <div className={styles.scopeContainer}>
                                        <ProcessTemplateParameterScopeCell parameter={data.Parameter} value={data.Value} onChange={(parameter) => onParameterChange(parameter, data.ParameterIndex)} index={data.ValueIndex} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} isEditing={isEditingScope} setIsEditing={setIsEditingScope} scopeValues={availableScopeValues} height={64} width={scopeWidth} isConsumer={false}/>
                                    </div>
                                    <OverflowMenu menuItems={[
                        OverflowMenuItems.item("Delete value", async () => deleteValue(data.ParameterIndex, data.ValueIndex), {
                            permission: Permission.PlatformHubEdit,
                        }),
                    ]}/>
                                </div>) : null,
                width: "40%",
                padding: "0",
                ref: scopeHeaderRef,
            },
        ]} getRowKey={(data) => `${data.ParameterIndex}-${data.ValueIndex}`}/>
        </div>);
}
interface ProcessTemplateParameterNameCellProps {
    data: ParameterValueRow;
    onOpenEditParameterDialog: (parameter: ProcessTemplateParameterResource) => void;
    onDeleteParameter: (parameterIndex: number) => void;
}
function ProcessTemplateParameterNameCell({ data, onOpenEditParameterDialog, onDeleteParameter }: ProcessTemplateParameterNameCellProps) {
    return (<div className={styles.parameterNameCell}>
            <div className={styles.parameterNameAndHelpText}>
                <div className={styles.parameterName}>
                    <a href="" onClick={(e) => {
            e.preventDefault();
            onOpenEditParameterDialog(data.Parameter);
        }}>
                        <TemplateName name={data.Parameter.Name}/>
                    </a>
                    <div className={styles.parameterLabel}>{data.Parameter.Label}</div>
                </div>
                <TemplateHelpText text={data.Parameter.HelpText}/>
            </div>
            <OverflowMenu menuItems={[
            OverflowMenuItems.item("Edit parameter", () => onOpenEditParameterDialog(data.Parameter), { permission: Permission.PlatformHubEdit }),
            OverflowMenuItems.item("Delete parameter", () => onDeleteParameter(data.ParameterIndex), { permission: Permission.PlatformHubEdit }),
        ]}/>
        </div>);
}
interface ProcessTemplateParameterValueCellProps {
    parameter: ProcessTemplateParameterResource;
    value: ProcessTemplateParameterValueResource | null;
    onChange: (parameter: ProcessTemplateParameterResource) => void;
    platformHubAccounts: PlatformHubAccountResource[];
    focusedValueKey: ProcessTemplateValueKey | null;
    setFocusedValueKey: (key: ProcessTemplateValueKey | null) => void;
    valueIndex: number;
}
function ProcessTemplateParameterValueCell({ parameter, value, onChange, platformHubAccounts, focusedValueKey, setFocusedValueKey, valueIndex }: ProcessTemplateParameterValueCellProps) {
    const { doBusyTask } = useLegacyDoBusyTask();
    const onValueChange = (newParameter: ProcessTemplateParameterResource) => {
        onChange(newParameter);
    };
    const addValue = () => {
        onChange({ ...parameter, Values: [...parameter.Values, { Value: "", Scope: {} }] });
    };
    const valueKey = createProcessTemplateValueKey(parameter.Name, valueIndex, "Value");
    const onFocus = () => setFocusedValueKey(valueKey);
    const onBlur = () => setFocusedValueKey(null);
    const focused = focusedValueKey === valueKey;
    if (!value) {
        const controlType = parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
        if (controlType === ControlType.Package || controlType === ControlType.Certificate || controlType === ControlType.Sensitive || controlType === ControlType.WorkerPool || controlType === ControlType.StepName) {
            return <div className={cx(styles.disabledAddValue, styles.valueContainer)}>Values for this parameter type must be supplied by a project.</div>;
        }
        return (<div className={styles.valueContainer}>
                <Button label={"Add value"} onClick={() => addValue()} importance={"quiet"}/>
            </div>);
    }
    return (<div onFocus={onFocus} onClick={onFocus} className={styles.valueContainer}>
            <PopoverWhenFocused isFocused={focused} onClickOutside={onBlur} position={{ top: 0, left: -12, right: 0 }}>
                {focused ? (<div className={styles.popoverContainer}>
                        <ProcessTemplateParameterValueInput parameter={parameter} accounts={platformHubAccounts} localNames={[]} doBusyTask={doBusyTask} value={value.Value} onChange={(value) => onValueChange({ ...parameter, Values: parameter.Values.map((v, i) => (i === valueIndex ? { ...v, Value: value ?? "" } : v)) })} type={parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText} autoFocus={true}/>
                    </div>) : value.Value ? (<ReadonlyInlineVariableValue value={value.Value} variableType={getVariableTypeFromDisplaySettings(parameter.DisplaySettings["Octopus.ControlType"])} defaultValue={undefined} accounts={platformHubAccounts.reduce((acc, a) => ({ ...acc, [a.Id]: a.Name }), {})} certificates={{}} workerPools={{}} hasEditPermissions={hasPermission(Permission.PlatformHubEdit)} sourceItems={undefined}/>) : (<Text value="" placeholder={"Enter a value"} onChange={noOp} hideUnderline={true} showValueAsTitleAttribute={true} monoSpacedFont={true}/>)}
            </PopoverWhenFocused>
        </div>);
}
interface ProcessTemplateParameterValueInputProps {
    type: ControlType;
    value: string;
    parameter: ProcessTemplateParameterResource;
    localNames: string[];
    doBusyTask: DoBusyTask;
    accounts: PlatformHubAccountResource[];
    onChange: (value: string | undefined) => void;
    autoFocus?: boolean;
}
function ProcessTemplateParameterValueInput({ doBusyTask, localNames, accounts, type, value, parameter, onChange, autoFocus }: ProcessTemplateParameterValueInputProps) {
    switch (type) {
        case ControlType.SingleLineText:
            return <SingleLineTextInput label={""} onChange={onChange} value={value} localNames={localNames} warning={undefined} defaultValueIndicator={undefined} monoSpacedFont={true} autoFocus={autoFocus}/>;
        case ControlType.MultiLineText:
            return <MultiLineTextInput label={""} onChange={onChange} value={value} localNames={localNames} warning={undefined} defaultValueIndicator={undefined} monoSpacedFont={true} autoFocus={autoFocus}/>;
        case ControlType.Select:
            const options = selectOptionsToItems(parameter.DisplaySettings["Octopus.SelectOptions"]);
            return <SelectInput label={""} onChange={onChange} value={value} resetValue={""} options={options} localNames={localNames} warning={undefined} allowClear={false} defaultValueIndicator={undefined} doBusyTask={doBusyTask}/>;
        case ControlType.Checkbox:
            return <CheckboxInput label={""} onChange={onChange} value={value} resetValue={""} localNames={localNames} warning={undefined} defaultValueIndicator={undefined} doBusyTask={doBusyTask}/>;
        case ControlType.AmazonWebServicesAccount:
            return <PlatformHubAccountSelect onChange={onChange} value={value} accounts={accounts} type={[AccountType.AmazonWebServicesAccount, AccountType.AmazonWebServicesOidcAccount]}/>;
        case ControlType.AzureAccount:
            return <PlatformHubAccountSelect onChange={onChange} value={value} accounts={accounts} type={[AccountType.AzureServicePrincipal, AccountType.AzureSubscription, AccountType.AzureOidc]}/>;
        case ControlType.GoogleCloudAccount:
            return <PlatformHubAccountSelect onChange={onChange} value={value} accounts={accounts} type={[AccountType.GoogleCloudAccount]}/>;
        case ControlType.UsernamePasswordAccount:
            return <PlatformHubAccountSelect onChange={onChange} value={value} accounts={accounts} type={[AccountType.UsernamePassword]}/>;
        case ControlType.Sensitive:
            throw new Error("Sensitive values are not supported in process templates");
        case ControlType.Certificate:
            throw new Error("Certificate values are currently not supported in process templates");
        case ControlType.WorkerPool:
            throw new Error("Worker pool values are currently not supported in process templates");
        case ControlType.Package:
            throw new Error("Package values are currently not supported in process templates");
        case ControlType.StepName:
            throw new Error("Step name values are currently not supported in process templates");
        default:
            return (<React.Fragment>
                    <DebounceText value={value} warning={undefined} onChange={onChange}/>
                </React.Fragment>);
    }
}
interface PlatformHubAccountSelectProps {
    onChange: (value: string | undefined) => void;
    value: string | undefined;
    accounts: PlatformHubAccountResource[];
    type: AccountType[];
}
function PlatformHubAccountSelect({ onChange, value, accounts, type }: PlatformHubAccountSelectProps) {
    const accountItems = accounts.filter((a) => type.includes(a.AccountType)).map((a) => ({ value: a.Id, text: a.Name }));
    return (<div className={styles.accountSelectContainer}>
            <Select onChange={onChange} value={value} items={accountItems}/>
        </div>);
}
interface ProcessTemplateParameterScopeCellProps {
    parameter: ProcessTemplateParameterResource;
    value: ProcessTemplateParameterValueResource;
    onChange: (parameter: ProcessTemplateParameterResource) => void;
    focusedValueKey: ProcessTemplateValueKey | null;
    setFocusedValueKey: (key: ProcessTemplateValueKey | null) => void;
    isEditing: boolean;
    setIsEditing?: (isEditing: boolean) => void;
    index: number;
    scopeValues: ScopeValues;
    height: number;
    width: number;
    isConsumer: boolean;
}
export function ProcessTemplateParameterScopeCell({ parameter, value, onChange, focusedValueKey, setFocusedValueKey, isEditing, setIsEditing, index, scopeValues, height, width, isConsumer }: ProcessTemplateParameterScopeCellProps) {
    const { doBusyTask } = useLegacyDoBusyTask();
    const onScopeChange = (newScope: ScopeSpecification, valueIndex: number) => {
        const newParameter = {
            ...parameter,
            Values: parameter.Values.map((v, i) => i === valueIndex
                ? {
                    ...v,
                    Scope: {
                        Environment: newScope.Environment ? [...newScope.Environment] : undefined,
                    },
                }
                : v),
        };
        onChange(newParameter);
    };
    const variableType = getVariableTypeFromDisplaySettings(parameter.DisplaySettings["Octopus.ControlType"]);
    const valueKey = createProcessTemplateValueKey(parameter.Name, index, "Scope");
    const noScopes = Object.values(value.Scope).reduce((acc, scope) => acc + (scope?.length ?? 0), 0) === 0;
    if (isConsumer && noScopes) {
        return null;
    }
    const readOnly = isConsumer;
    return (<VariableScopeCell scope={value.Scope} onScopeChanged={(scope) => onScopeChange(scope, index)} availableScopes={scopeValues} tagIndex={{}} variableType={variableType} isEditing={valueKey === focusedValueKey && isEditing} isViewingAll={valueKey === focusedValueKey} doBusyTask={doBusyTask} onFocusEdit={() => {
            setFocusedValueKey(valueKey);
            setIsEditing && setIsEditing(true);
        }} onBlurEdit={() => {
            setFocusedValueKey(null);
            setIsEditing && setIsEditing(false);
        }} onFocusViewAll={() => setFocusedValueKey(valueKey)} onBlurViewAll={() => setFocusedValueKey(null)} onOpenEditorClicked={() => setIsEditing && setIsEditing(true)} cellHeight={height} containerWidth={width} editorScope={"PlatformHub"} deleted={readOnly}/>);
}
const styles = {
    parameterNameCell: css({
        display: "flex",
        width: "100%",
        justifyContent: "space-between",
        alignItems: "center",
    }),
    parameterNameAndHelpText: css({
        display: "flex",
        alignItems: "center",
        gap: space[8],
    }),
    parameterName: css({
        font: text.body.bold.medium,
    }),
    parameterLabel: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.tertiary,
    }),
    parameterValues: css({
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
    disabledAddValue: css({
        color: themeTokens.color.text.disabled,
    }),
    scopeAndOverflowMenuContainer: css({
        display: "flex",
        width: "100%",
        gap: space[4],
        alignItems: "center",
        height: "4rem",
    }),
    valueContainer: css({
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: "4rem",
        paddingLeft: space[12],
        paddingRight: space[12],
    }),
    scopeContainer: css({
        flexGrow: 1,
        height: "100%",
    }),
    popoverContainer: css({
        paddingLeft: space[12],
        paddingRight: space[12],
        width: "100%",
    }),
    accountSelectContainer: css({
        height: "4rem",
    }),
};
interface AddProcessTemplateParameterButtonProps {
    parameters: ProcessTemplateParameterResource[];
    onAdd: (parameter: ActionTemplateParameterResource) => void;
    type: ActionButtonType;
}
export function AddProcessTemplateParameterButton({ parameters, onAdd, type }: AddProcessTemplateParameterButtonProps) {
    return (<PermissionCheck key="AddParameterPermission" {...parameterEditPermission}>
            <OpenDialogButton key="AddParameter" label={`Add parameter`} type={type}>
                <ProcessTemplateParameterEditorDialog allParameters={parameters} onOk={onAdd}/>
            </OpenDialogButton>
        </PermissionCheck>);
}
interface OpenReorderParametersButtonProps {
    parameters: ProcessTemplateParameterResource[];
    onChange: (parameters: ActionTemplateParameterResource[]) => void;
}
function OpenReorderParametersButton({ parameters, onChange }: OpenReorderParametersButtonProps) {
    return <SortActionTemplateParametersButton parameters={parameters} onChange={onChange} permission={parameterEditPermission} name={"parameter"}/>;
}
interface ParametersOnboardingProps {
    parameters: ProcessTemplateParameterResource[];
    onAdd: (parameter: ActionTemplateParameterResource) => void;
}
function ParametersOnboarding({ onAdd, parameters }: ParametersOnboardingProps) {
    return (<OnboardingPage title="Add process template parameters" intro={<div>Parameters define variables that are set when the process template is included in a project deployment process.</div>} actionButtons={<AddProcessTemplateParameterButton parameters={parameters} onAdd={onAdd} type={ActionButtonType.Primary}/>} learnMore={null}/>);
}
const parameterEditPermission = {
    permission: Permission.PlatformHubEdit,
};
export function buildProcessTemplateParametersActions(parameters: ProcessTemplateParameterResource[], onChange: (parameters: ActionTemplateParameterResource[]) => void): PageAction[] {
    const onAdd = (parameter: ActionTemplateParameterResource) => onChange([...parameters, parameter]);
    const reorderParametersAction: PageAction = {
        type: "custom",
        content: <OpenReorderParametersButton parameters={parameters} onChange={onChange}/>,
        key: "Reorder Parameters",
    };
    const addParameterAction: PageAction = {
        type: "custom",
        content: <AddProcessTemplateParameterButton parameters={parameters} onAdd={onAdd} type={ActionButtonType.Secondary}/>,
        key: "Add Parameter",
    };
    return [reorderParametersAction, addParameterAction];
}
const defaultScopeValues: ScopeValues = {
    Actions: [],
    Channels: [],
    Environments: [],
    Machines: [],
    Roles: [],
    TenantTags: [],
    Processes: [],
};
export type ProcessTemplateValueKey = `${string};${string};${string}`;
export function createProcessTemplateValueKey(parameterName: string, index: number, type: "Value" | "Scope"): ProcessTemplateValueKey {
    return `${parameterName};${index};${type}`;
}
